import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, PageHeader, StatusBadge } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

export default function MyApplications() {
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [showAllInterviews, setShowAllInterviews] = useState(false);
  const [offerFor, setOfferFor] = useState(null);
  const [offerNote, setOfferNote] = useState("");
  const [offerAction, setOfferAction] = useState("accept");

  const load = () => {
    api.get("/candidate/applications").then(({ data }) => setApps(data.applications)).catch(() => setApps([]));
    api.get("/candidate/interviews").then(({ data }) => setInterviews(data.interviews)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const pickSlot = async (ivId, slot) => {
    try {
      await api.post(`/interviews/${ivId}/pick-slot`, { slot });
      toast.success("Slot confirmed — interview scheduled");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const respondOffer = async () => {
    try {
      await api.post(`/applications/${offerFor.id}/offer/respond`, { action: offerAction, note: offerNote });
      toast.success(`Offer ${offerAction}ed`);
      setOfferFor(null);
      setOfferNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!apps) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="my-applications-page">
      <PageHeader title="My Applications" description="Track every application and interview in one place." />

      {interviews.length > 0 && (
        <div className="bg-white border border-border/60 rounded-xl p-6 mb-8" data-testid="interviews-card">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4 flex items-center gap-2">
            <CalendarCheck className="w-4 h-4 text-[#2563EB]" /> Interviews
          </h3>
          <div className="space-y-3">
            {[...interviews]
              .sort((a, b) => (a.status === "awaiting_candidate" ? -1 : 0) - (b.status === "awaiting_candidate" ? -1 : 0))
              .slice(0, showAllInterviews ? undefined : 8)
              .map((iv) => (
              <div key={iv.id} className="border border-border/60 rounded-lg p-4" data-testid={`interview-${iv.id}`}>
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">{iv.job_title} — {iv.round}</p>
                    {iv.status === "awaiting_candidate" ? (
                      <p className="text-xs text-[#2563EB] font-semibold mt-1">Pick your preferred slot below</p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1 capitalize">{iv.mode} · {new Date(iv.scheduled_at).toLocaleString("en-IN")}</p>
                    )}
                    {iv.panel?.length > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1">Panel: {iv.panel.join(", ")}</p>
                    )}
                  </div>
                  {iv.meeting_link && iv.status !== "awaiting_candidate" && (
                    <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-sm text-[#2563EB] font-semibold" data-testid={`join-${iv.id}`}>
                      Join meeting →
                    </a>
                  )}
                </div>
                {iv.status === "awaiting_candidate" && (
                  <div className="flex gap-2 flex-wrap mt-3">
                    {(iv.proposed_slots || []).map((s) => (
                      <Button key={s} size="sm" variant="outline" className="rounded-full" onClick={() => pickSlot(iv.id, s)} data-testid={`slot-${iv.id}-${s}`}>
                        {new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {interviews.length > 8 && (
            <button
              className="text-sm text-[#2563EB] font-medium mt-4"
              onClick={() => setShowAllInterviews((s) => !s)}
              data-testid="interviews-toggle"
            >
              {showAllInterviews ? "Show fewer" : `Show all ${interviews.length} interviews`}
            </button>
          )}
        </div>
      )}

      {apps.length === 0 ? (
        <EmptyState
          title="No applications yet."
          description="Explore open roles tailored to your profile."
          cta="Explore Jobs"
          onCta={() => navigate("/jobs")}
          testId="applications-empty"
        />
      ) : (
        <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="applications-table">
            <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-5 py-3.5 font-semibold">Job</th>
                <th className="px-5 py-3.5 font-semibold">Company</th>
                <th className="px-5 py-3.5 font-semibold">Applied</th>
                <th className="px-5 py-3.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-b border-border/40 hover:bg-[#F8FAFC]" data-testid={`application-row-${a.id}`}>
                  <td className="px-5 py-4 font-semibold text-[#0B1B3D]">{a.job_title}</td>
                  <td className="px-5 py-4">{a.company_name}</td>
                  <td className="px-5 py-4 text-xs text-muted-foreground">{fmtDate(a.created_at)}</td>
                  <td className="px-5 py-4">
                    <StatusBadge status={a.stage} />
                    {a.stage === "offer" && !a.offer_response && (
                      <div className="flex gap-1.5 mt-2">
                        <Button size="sm" className="h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => { setOfferFor(a); setOfferAction("accept"); }} data-testid={`offer-accept-${a.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-full text-red-600 border-red-200 text-xs" onClick={() => { setOfferFor(a); setOfferAction("decline"); }} data-testid={`offer-decline-${a.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Decline
                        </Button>
                      </div>
                    )}
                    {a.offer_response && (
                      <p className={`text-xs font-semibold mt-1.5 capitalize ${a.offer_response === "accepted" ? "text-emerald-700" : "text-red-600"}`} data-testid={`offer-status-${a.id}`}>
                        Offer {a.offer_response}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!offerFor} onOpenChange={() => setOfferFor(null)}>
        <DialogContent data-testid="offer-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{offerAction === "accept" ? "Accept" : "Decline"} offer — {offerFor?.job_title}</DialogTitle>
          </DialogHeader>
          <Textarea rows={3} value={offerNote} onChange={(e) => setOfferNote(e.target.value)} placeholder="Note to the employer (optional)" data-testid="offer-note" />
          <Button
            onClick={respondOffer}
            className={`w-full rounded-full ${offerAction === "accept" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
            data-testid="offer-confirm-btn"
          >
            Confirm {offerAction === "accept" ? "Acceptance" : "Decline"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
